import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  startYoutubeSync,
  runYoutubeSyncBatch,
  getSyncRun,
  listSyncRuns,
  cancelYoutubeSync,
} from "@/lib/admin.functions";
import {
  SYNC_CATEGORY_OPTIONS,
  DEFAULT_SYNC_CATEGORIES,
  categoryLabel,
} from "@/lib/sync-categories";
import {
  summarizeDiscards,
  skipReasonLabel,
  AUDIT_STOP_LABELS,
  type AuditStopCode,
} from "@/lib/sync-audit";

import { Button } from "@/components/ui/button";
import {
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  MinusCircle,
  Plus,
  StopCircle,
  Filter,
  ShieldCheck,
} from "lucide-react";

import { toast } from "sonner";
import { formatDateTimeBR, formatTimeBR } from "@/lib/datetime";

export const Route = createFileRoute("/_authenticated/admin/sincronizar")({
  component: SyncPage,
});

type ItemStatus = "pending" | "inserted" | "updated" | "skipped" | "failed";
type RunStatus = "running" | "success" | "partial" | "failed";

function SyncPage() {
  const startFn = useServerFn(startYoutubeSync);
  const batchFn = useServerFn(runYoutubeSyncBatch);
  const runFn = useServerFn(getSyncRun);
  const listFn = useServerFn(listSyncRuns);
  const cancelFn = useServerFn(cancelYoutubeSync);
  const queryClient = useQueryClient();

  const [maxPages, setMaxPages] = useState(3);
  const [categories, setCategories] = useState<string[]>(DEFAULT_SYNC_CATEGORIES);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | ItemStatus>("all");
  const [reasonFilter, setReasonFilter] = useState<string | null>(null);

  const [isRunning, setIsRunning] = useState(false);
  const abortRef = useRef(false);

  const toggleCategory = (key: string) =>
    setCategories((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const historyQuery = useQuery({
    queryKey: ["sync-runs"],
    queryFn: () => listFn(),
    refetchInterval: activeRunId ? 3000 : false,
  });

  // Pre-select the latest run for viewing
  useEffect(() => {
    if (!activeRunId && historyQuery.data?.[0]) {
      setActiveRunId((historyQuery.data[0] as any).id);
    }
  }, [historyQuery.data, activeRunId]);

  const runQuery = useQuery({
    queryKey: ["sync-run", activeRunId],
    queryFn: () => runFn({ data: { runId: activeRunId! } }),
    enabled: !!activeRunId,
    refetchInterval: (q) => {
      const status = (q.state.data as any)?.run?.status as RunStatus | undefined;
      return status === "running" ? 1500 : false;
    },
  });

  const startMutation = useMutation({
    mutationFn: async () => {
      abortRef.current = false;
      setIsRunning(true);
      const { runId } = await startFn({ data: { maxPages, categories } });
      setActiveRunId(runId);
      queryClient.invalidateQueries({ queryKey: ["sync-runs"] });

      let done = false;
      let batches = 0;
      let auditCode: string | undefined;
      while (!done && !abortRef.current) {
        const res: any = await batchFn({ data: { runId } });
        done = res.done;
        if (res.audit?.code) auditCode = res.audit.code;
        batches++;
        queryClient.invalidateQueries({ queryKey: ["sync-run", runId] });
        queryClient.invalidateQueries({ queryKey: ["sync-runs"] });

        // A auditoria automática no servidor decide quando encerrar (erro,
        // tempo excedido, fim do canal ou lotes sem novidades).
        // Safety cap local: maxPages + 5 lotes.
        if (batches > maxPages + 5) break;
      }
      if (abortRef.current) {
        await cancelFn({ data: { runId } }).catch(() => {});
      }
      queryClient.invalidateQueries();
      return { runId, aborted: abortRef.current, auditCode };
    },
    onSuccess: (res) => {
      setIsRunning(false);
      if (res.aborted) toast.warning("Sincronização interrompida");
      else if (res.auditCode === "no_progress")
        toast.info("Auditoria: sem novidades nos últimos lotes — sincronização encerrada.");
      else if (res.auditCode === "failure_rate" || res.auditCode === "timeout")
        toast.error("Auditoria interrompeu a sincronização. Veja o motivo no painel.");
      else toast.success("Sincronização concluída");
    },

    onError: (e: any) => {
      setIsRunning(false);
      queryClient.invalidateQueries({ queryKey: ["sync-runs"] });
      toast.error(e.message || "Erro na sincronização");
    },
  });

  const stopSync = () => {
    abortRef.current = true;
    if (activeRunId) cancelFn({ data: { runId: activeRunId } }).catch(() => {});
    toast.info("Interrompendo sincronização…");
  };

  const run = runQuery.data?.run;
  const items = runQuery.data?.items ?? [];
  const filteredItems = useMemo(() => {
    let list = filter === "all" ? items : items.filter((i: any) => i.status === filter);
    if (reasonFilter) list = list.filter((i: any) => i.skip_reason === reasonFilter);
    return list;
  }, [items, filter, reasonFilter]);
  const discards = useMemo(() => summarizeDiscards(run?.discard_stats), [run?.discard_stats]);

  const progress = run
    ? run.total_videos > 0
      ? Math.round((run.processed_count / run.total_videos) * 100)
      : run.status === "running"
        ? 5
        : 100
    : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl font-semibold text-primary">
          Sincronizar com o YouTube
        </h1>
        <p className="mt-1 text-muted-foreground">
          Importa <strong>somente transmissões ao vivo já encerradas</strong> do canal{" "}
          <strong>@rechovot</strong>
          (lives finalizadas do Shabat) e registra o resultado de cada vídeo em uma fila com
          progresso e log.
        </p>
      </div>

      {/* Controls */}
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-sm font-medium" htmlFor="sync-max-pages">
              Páginas (50 vídeos cada)
            </label>
            <input
              id="sync-max-pages"
              aria-label="Páginas de 50 vídeos a processar"
              type="number"
              inputMode="numeric"
              min={1}
              max={200}
              value={maxPages === 0 ? "" : maxPages}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "") {
                  setMaxPages(0);
                  return;
                }
                const n = parseInt(v, 10);
                if (!Number.isNaN(n)) setMaxPages(Math.min(200, Math.max(0, n)));
              }}
              onBlur={() => {
                if (!maxPages || maxPages < 1) setMaxPages(1);
              }}
              className="mt-2 block w-28 rounded-md border border-input bg-background px-3 py-2 text-sm"
              disabled={startMutation.isPending}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Total: até {maxPages * 50} vídeos (até ~10.000 vídeos; canal tem ~4.200).
            </p>
          </div>
          <Button
            onClick={() => startMutation.mutate()}
            disabled={startMutation.isPending}
            size="lg"
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${startMutation.isPending ? "animate-spin" : ""}`}
            />
            {startMutation.isPending ? "Sincronizando..." : "Iniciar sincronização"}
          </Button>
          {startMutation.isPending && (
            <Button onClick={stopSync} variant="destructive" size="lg" disabled={abortRef.current}>
              <StopCircle className="mr-2 h-4 w-4" />
              Interromper
            </Button>
          )}
        </div>

        <CategoryFilters
          selected={categories}
          onToggle={toggleCategory}
          onSetAll={setCategories}
          disabled={startMutation.isPending}
        />

        <NextSyncInfo />
      </div>

      {/* History picker */}
      {historyQuery.isLoading && (
        // Espaço reservado com a mesma altura da lista de execuções recentes,
        // para a página não "pular" quando o histórico carrega.
        <div
          className="h-24 animate-pulse rounded-xl bg-secondary"
          role="status"
          aria-label="Carregando"
        />
      )}
      {historyQuery.data && historyQuery.data.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Execuções recentes
          </div>
          <div className="flex flex-wrap gap-2">
            {historyQuery.data.map((r: any) => (
              <button
                key={r.id}
                onClick={() => setActiveRunId(r.id)}
                className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                  activeRunId === r.id
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-background text-muted-foreground hover:border-primary/40"
                }`}
              >
                <RunStatusIcon status={r.status} />
                {formatDateTime(r.started_at)}
                <span className="text-[10px] text-muted-foreground">
                  {r.processed_count}/{r.total_videos || "?"}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Active run panel */}
      {run && (
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <RunStatusIcon status={run.status} />
              <span className="font-serif text-lg font-semibold text-primary">
                {statusLabel(run.status)}
              </span>
            </div>
            <div className="text-xs text-muted-foreground">
              Início: {formatDateTime(run.started_at)}
              {run.finished_at && ` · Fim: ${formatDateTime(run.finished_at)}`}
              {run.finished_at && (
                <> · Duração: {formatDuration(run.started_at, run.finished_at)}</>
              )}
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-4">
            <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {run.processed_count} / {run.total_videos || "?"} vídeos
              </span>
              <span>{progress}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-secondary">
              <div
                className={`h-full transition-all ${
                  run.status === "failed"
                    ? "bg-destructive"
                    : run.status === "partial"
                      ? "bg-gold"
                      : "bg-primary"
                }`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Counters */}
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-5">
            <Counter label="Novos" value={run.inserted_count} tone="primary" icon={Plus} />
            <Counter
              label="Atualizados"
              value={run.updated_count}
              tone="primary"
              icon={RefreshCw}
            />
            <Counter label="Pulados" value={run.skipped_count} tone="muted" icon={MinusCircle} />
            <Counter label="Falhas" value={run.failed_count} tone="destructive" icon={XCircle} />
            <Counter label="Total" value={run.total_videos} tone="muted" icon={Clock} />
          </div>

          {run.audit_stop_reason && (
            <div className="mt-4 flex items-start gap-2 rounded-lg border border-border bg-background/60 p-3 text-sm">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div>
                <div className="font-medium text-foreground">
                  Auditoria automática:{" "}
                  {AUDIT_STOP_LABELS[run.audit_stop_reason as AuditStopCode] ??
                    run.audit_stop_reason}
                </div>
                <p className="text-xs text-muted-foreground">
                  A execução foi encerrada automaticamente para não permanecer ativa sem
                  necessidade.
                </p>
              </div>
            </div>
          )}

          {run.error_message && (
            <div className="mt-4 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              {run.error_message}
            </div>
          )}

          <DiscardSummary
            discards={discards}
            activeReason={reasonFilter}
            onSelectReason={(reason) => {
              setReasonFilter(reason);
              if (reason) setFilter("all");
            }}
          />

          {/* Filter tabs */}
          <div className="mt-6 flex flex-wrap gap-1 border-b border-border">
            {(
              [
                { k: "all", label: `Todos (${items.length})` },
                { k: "inserted", label: `Novos (${run.inserted_count})` },
                { k: "updated", label: `Atualizados (${run.updated_count})` },
                { k: "failed", label: `Falhas (${run.failed_count})` },
                { k: "skipped", label: `Pulados (${run.skipped_count})` },
              ] as const
            ).map((t) => (
              <button
                key={t.k}
                onClick={() => setFilter(t.k as any)}
                className={`px-3 py-2 text-xs font-medium transition-colors ${
                  filter === t.k
                    ? "border-b-2 border-primary text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {reasonFilter && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>
                Mostrando apenas descartes por:{" "}
                <strong className="text-foreground">{skipReasonLabel(reasonFilter)}</strong>
              </span>
              <button
                onClick={() => setReasonFilter(null)}
                className="rounded-full border border-border px-2 py-0.5 hover:border-primary/40"
              >
                limpar
              </button>
            </div>
          )}

          {/* Item log */}
          <div className="mt-3 max-h-[500px] overflow-y-auto">
            {filteredItems.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                {run.status === "running"
                  ? "Aguardando processamento…"
                  : "Nenhum item nesta categoria."}
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {filteredItems.map((item: any) => (
                  <li key={item.id} className="flex items-start gap-3 py-3 text-sm">
                    <ItemStatusIcon status={item.status} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-foreground">
                        {item.title || item.youtube_video_id || "Sem título"}
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {item.message}
                        {item.parasha_matched && ` · Parashá: ${item.parasha_matched}`}
                        {item.category && ` · ${categoryLabel(item.category)}`}
                        {item.needs_review && (
                          <span className="ml-2 rounded-full bg-gold/20 px-1.5 py-0.5 text-[10px] font-medium text-gold-foreground">
                            Requer revisão
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="shrink-0 text-right text-[10px] text-muted-foreground">
                      {formatTime(item.processed_at)}
                      {item.youtube_video_id && (
                        <div className="mt-0.5">
                          <a
                            href={`https://youtu.be/${item.youtube_video_id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary hover:underline"
                          >
                            abrir
                          </a>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {!historyQuery.isLoading && (historyQuery.data?.length ?? 0) === 0 && (
        <div className="rounded-xl border border-gold/40 bg-gold/5 p-4">
          <div className="flex gap-3 text-sm">
            <AlertCircle className="h-5 w-5 shrink-0 text-gold" />
            <div>
              <p className="font-medium text-foreground">Nenhuma sincronização executada ainda</p>
              <p className="mt-1 text-muted-foreground">
                Configure a chave <code className="rounded bg-muted px-1">YOUTUBE_API_KEY</code> nos
                secrets e clique em "Iniciar sincronização" para importar os vídeos.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CategoryFilters({
  selected,
  onToggle,
  onSetAll,
  disabled,
}: {
  selected: string[];
  onToggle: (key: string) => void;
  onSetAll: (keys: string[]) => void;
  disabled?: boolean;
}) {
  const groups = ["Ensinos", "Festividades", "Outros"] as const;
  return (
    <div className="mt-5 rounded-lg border border-border bg-background/50 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Filter className="h-4 w-4 text-primary" />
            Filtrar vídeos a sincronizar
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Categorias identificadas pelo título dos vídeos do canal. Vídeos sem padrão reconhecível
            entram em <strong>Outros / sem padrão</strong>.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() => onSetAll(SYNC_CATEGORY_OPTIONS.map((o) => o.key))}
          >
            Selecionar tudo
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => onSetAll([])}
          >
            Limpar
          </Button>
        </div>
      </div>

      <div className="mt-4 space-y-4">
        {groups.map((g) => (
          <div key={g}>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {g}
            </div>
            <div className="flex flex-wrap gap-2">
              {SYNC_CATEGORY_OPTIONS.filter((o) => o.group === g).map((o) => {
                const active = selected.includes(o.key);
                return (
                  <button
                    key={o.key}
                    type="button"
                    disabled={disabled}
                    onClick={() => onToggle(o.key)}
                    title={o.description}
                    className={`rounded-full border px-3 py-1.5 text-left text-xs transition-colors disabled:opacity-60 ${
                      active
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-background text-muted-foreground hover:border-primary/40"
                    }`}
                  >
                    <span className="font-medium">{o.label}</span>
                    {o.description && (
                      <span className="ml-1 hidden opacity-70 sm:inline">· {o.description}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <p className="mt-3 text-xs text-muted-foreground">
        {selected.length === 0
          ? "Nenhum filtro selecionado — todos os vídeos elegíveis serão avaliados."
          : `${selected.length} filtro(s) ativo(s): ${selected.map(categoryLabel).join(", ")}.`}
      </p>
    </div>
  );
}

function NextSyncInfo() {
  // A sincronização automática roda a cada 10 minutos (agendamento do servidor).
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const next = useMemo(() => {
    const d = new Date(now);
    d.setSeconds(0, 0);
    d.setMinutes(Math.floor(now.getMinutes() / 10) * 10 + 10);
    return d;
  }, [now]);

  const diffMs = Math.max(0, next.getTime() - now.getTime());
  const mm = Math.floor(diffMs / 60000);
  const ss = Math.floor((diffMs % 60000) / 1000);

  return (
    <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-background/50 p-3 text-sm">
      <Clock className="h-4 w-4 text-primary" />
      <span className="text-muted-foreground">Próxima sincronização automática:</span>
      <strong className="text-foreground">{formatTimeBR(next)}</strong>
      <span className="text-xs text-muted-foreground">
        (em {mm}m {String(ss).padStart(2, "0")}s · a cada 10 minutos)
      </span>
    </div>
  );
}

function Counter({
  label,
  value,
  tone,
  icon: Icon,
}: {
  label: string;
  value: number;
  tone: "primary" | "muted" | "destructive";
  icon: any;
}) {
  const toneClass =
    tone === "primary"
      ? "text-primary"
      : tone === "destructive"
        ? "text-destructive"
        : "text-muted-foreground";
  return (
    <div className="rounded-lg border border-border bg-background/50 p-3">
      <div className={`flex items-center gap-1.5 text-xs ${toneClass}`}>
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className={`mt-1 font-serif text-2xl font-semibold ${toneClass}`}>{value}</div>
    </div>
  );
}

function RunStatusIcon({ status }: { status: RunStatus }) {
  if (status === "running") return <RefreshCw className="h-4 w-4 animate-spin text-primary" />;
  if (status === "success") return <CheckCircle2 className="h-4 w-4 text-primary" />;
  if (status === "partial") return <AlertCircle className="h-4 w-4 text-gold" />;
  return <XCircle className="h-4 w-4 text-destructive" />;
}

function ItemStatusIcon({ status }: { status: ItemStatus }) {
  const base = "h-4 w-4 shrink-0 mt-0.5";
  if (status === "inserted") return <Plus className={`${base} text-primary`} />;
  if (status === "updated") return <RefreshCw className={`${base} text-primary`} />;
  if (status === "skipped") return <MinusCircle className={`${base} text-muted-foreground`} />;
  if (status === "failed") return <XCircle className={`${base} text-destructive`} />;
  return <Clock className={`${base} text-muted-foreground`} />;
}

function statusLabel(status: RunStatus): string {
  return {
    running: "Sincronização em andamento",
    success: "Sincronização concluída",
    partial: "Concluída com falhas",
    failed: "Falha na sincronização",
  }[status];
}

function formatDateTime(iso: string) {
  return formatDateTimeBR(iso, {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatTime(iso: string) {
  return formatTimeBR(iso, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function formatDuration(startIso: string, endIso: string) {
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  return `${m}m ${rs}s`;
}

function DiscardSummary({
  discards,
  activeReason,
  onSelectReason,
}: {
  discards: ReturnType<typeof summarizeDiscards>;
  activeReason: string | null;
  onSelectReason: (reason: string | null) => void;
}) {
  if (discards.total === 0) return null;
  return (
    <div className="mt-5 rounded-xl border border-border bg-background/60 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Filter className="h-4 w-4 text-primary" />
          Vídeos descartados nesta execução
        </h2>
        <span className="text-xs text-muted-foreground">
          {discards.total} descarte(s) — clique em um motivo para filtrar o log
        </span>
      </div>

      <ul className="mt-3 space-y-2">
        {discards.rows.map((row) => {
          const active = activeReason === row.reason;
          return (
            <li key={row.reason}>
              <button
                onClick={() => onSelectReason(active ? null : row.reason)}
                className={`w-full rounded-lg border p-3 text-left transition-colors ${
                  active
                    ? "border-primary/60 bg-primary/5"
                    : "border-border hover:border-primary/40"
                }`}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm font-medium text-foreground">{row.label}</span>
                  <span className="text-sm font-semibold tabular-nums text-primary">
                    {row.total}
                  </span>
                </div>
                {row.hint && <p className="mt-0.5 text-xs text-muted-foreground">{row.hint}</p>}
                {row.categories.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {row.categories.map((c) => (
                      <span
                        key={c.category}
                        className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground"
                      >
                        {categoryLabel(c.category as any)}: {c.count}
                      </span>
                    ))}
                  </div>
                )}
              </button>
            </li>
          );
        })}
      </ul>

      {discards.byCategory.length > 0 && (
        <div className="mt-3 border-t border-border pt-3">
          <p className="text-xs font-medium text-muted-foreground">Total por categoria</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {discards.byCategory.map((c) => (
              <span
                key={c.category}
                className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground"
              >
                {categoryLabel(c.category as any)}: {c.count}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
